/**
 * Maia 3 move vocabulary (4352 entries).
 *
 * Layout (verified against CSSLab's published all_moves_maia3.json):
 *   0..4095   — all from-to square pairs, in square-index order
 *               (a1,b1,...,h1,a2,...,h8) × same.
 *   4096..4351 — promotion moves, enumerated as
 *               (from-file × to-file × {q,r,b,n}), always from rank 7 → rank 8.
 *
 * Moves for black are passed through after a board mirror (see tensor.ts);
 * the vocabulary is from White's perspective always.
 *
 * Generated deterministically so we don't vendor CSSLab's JSON data file.
 */

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const RANKS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;
const PROMOS = ['q', 'r', 'b', 'n'] as const;

/** All 64 squares in the model's ordering: a1,b1,...,h1,a2,...,h8. */
export const SQUARES: readonly string[] = (() => {
  const out: string[] = [];
  for (const r of RANKS) for (const f of FILES) out.push(f + r);
  return out;
})();

/** All 4352 UCI move strings in the order the model outputs them. */
export const MOVE_VOCAB: readonly string[] = (() => {
  const out: string[] = new Array(4352);
  let i = 0;
  for (const from of SQUARES) for (const to of SQUARES) out[i++] = from + to;
  for (const ff of FILES) {
    for (const tf of FILES) {
      for (const p of PROMOS) out[i++] = `${ff}7${tf}8${p}`;
    }
  }
  return out;
})();

/** Reverse lookup: UCI string → vocabulary index. */
export const MOVE_INDEX: ReadonlyMap<string, number> = (() => {
  const m = new Map<string, number>();
  MOVE_VOCAB.forEach((uci, idx) => m.set(uci, idx));
  return m;
})();

/**
 * Mirror a UCI move (swap rank from White's view to Black's or vice versa).
 * Used to transform Black's legal moves into Maia's White-oriented vocab,
 * and to transform the model's picked move back to Black's perspective.
 */
export function mirrorMove(uci: string): string {
  const flip = (sq: string) => sq[0] + String(9 - parseInt(sq[1], 10));
  const base = flip(uci.slice(0, 2)) + flip(uci.slice(2, 4));
  return uci.length === 5 ? base + uci[4] : base;
}
